import React from "react";
import { Landmark, GraduationCap, HeartPulse, Banknote, ShoppingBag, Zap, Car, Building2 } from "lucide-react";

const INDUSTRIES = [
  {
    icon: Landmark,
    title: "Government & Public Sector",
    clients: ["Commonwealth of Massachusetts", "Ohio Dept. of Taxation"],
    span: "md:col-span-2 md:row-span-2",
  },
  {
    icon: GraduationCap,
    title: "Higher Education",
    clients: ["California State University", "Cerritos College"],
    span: "md:col-span-2",
  },
  {
    icon: Banknote,
    title: "Financial Services",
    clients: ["CapitalOne", "Freddie Mac", "Canadian Imperial Bank of Commerce"],
    span: "md:col-span-2",
  },
  {
    icon: HeartPulse,
    title: "Healthcare",
    clients: ["Maimonides Medical Center", "Blue Cross Blue Shield of Alabama"],
    span: "md:col-span-2",
  },
  {
    icon: ShoppingBag,
    title: "Retail & Consumer",
    clients: ["Hannaford (Delhaize)", "Sonance", "Learning Care Group"],
    span: "md:col-span-2",
  },
  {
    icon: Car,
    title: "Auto, Auction & Fleet",
    clients: ["KAR Auction Services", "Enterprise Fleet Management"],
    span: "md:col-span-2",
  },
  {
    icon: Zap,
    title: "Utilities & Energy",
    clients: ["East Kentucky Power Cooperative"],
    span: "md:col-span-2",
  },
  {
    icon: Building2,
    title: "IT Services & Consulting",
    clients: ["IBM", "Keane Inc.", "Kearney Inc.", "Teradata Corporation"],
    span: "md:col-span-2",
  },
];

// Marquee row of client names for visual anchor
const CLIENT_LIST = [
  "Commonwealth of Massachusetts",
  "California State University",
  "Cerritos College",
  "Maimonides Medical Center",
  "CapitalOne",
  "Freddie Mac",
  "Ohio Department of Taxation",
  "IBM",
  "Hannaford",
  "Blue Cross Blue Shield",
  "Canadian Imperial Bank",
  "KAR Auction Services",
  "Enterprise Fleet Management",
  "Teradata",
];

export const Industries = () => {
  return (
    <section id="industries" className="relative py-24 md:py-32" data-testid="industries-section">
      <div className="container-app">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline-strong w-12" />
              <span className="label-eyebrow">03 · Industries Served</span>
            </div>
            <h2
              className="font-serif text-balance"
              style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)", lineHeight: 1.05, letterSpacing: "-0.01em", fontWeight: 600 }}
              data-testid="industries-headline"
            >
              Trusted across the public sector,
              <br />
              higher education and the Fortune 500.
            </h2>
          </div>
          <div className="lg:col-span-5 self-end">
            <p className="text-foreground/70 leading-relaxed" data-testid="industries-intro">
              Engagements span complex regulated environments — state government,
              public universities, financial services and healthcare — where
              uptime, security and audit-readiness are non-negotiable.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-8 gap-px bg-border auto-rows-[180px]" data-testid="industries-grid">
          {INDUSTRIES.map((ind, i) => {
            const Icon = ind.icon;
            return (
              <article
                key={i}
                className={`bg-background p-6 md:p-8 flex flex-col justify-between transition-colors duration-300 hover:bg-foreground hover:text-background group ${ind.span}`}
                data-testid={`industry-card-${i}`}
              >
                <Icon className="w-7 h-7 text-accent group-hover:text-background transition-colors" strokeWidth={1.4} />
                <div>
                  <h3 className="font-serif text-lg md:text-xl leading-tight mb-2" style={{ fontWeight: 600 }}>
                    {ind.title}
                  </h3>
                  <ul className="text-[12px] text-foreground/70 group-hover:text-background/70 leading-relaxed space-y-0.5">
                    {ind.clients.map((c) => (
                      <li key={c}>· {c}</li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>

        {/* Marquee strip */}
        <div className="mt-16 overflow-hidden border-y border-border py-5" data-testid="clients-marquee">
          <div className="marquee-track flex gap-12 whitespace-nowrap">
            {[...CLIENT_LIST, ...CLIENT_LIST].map((c, i) => (
              <span
                key={i}
                className="font-serif italic text-foreground/50 text-lg"
              >
                {c} <span className="text-accent mx-2">·</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Industries;
